const TRIAL_DURATION_DAYS = 14;
const IS_SUBSCRIBED_KEY = 'is_subscribed';

export function getTrialStartDate(): string | null {
  return localStorage.getItem('trial_start_date');
}

export function setTrialStartDate(date?: string): void {
  if (date) {
    localStorage.setItem('trial_start_date', date);
  } else {
    localStorage.setItem('trial_start_date', new Date().toISOString());
  }
}

export function getTrialDaysLeft(): number | null {
  const storedTrialStart = localStorage.getItem('trial_start_date');
  if (!storedTrialStart) {
    return TRIAL_DURATION_DAYS;
  }
  const trialStart = new Date(storedTrialStart);
  const trialEnd = new Date(trialStart);
  trialEnd.setDate(trialEnd.getDate() + TRIAL_DURATION_DAYS);
  const now = new Date();
  const daysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return daysLeft > 0 ? daysLeft : null;
}

export function isTrialExpired(): boolean {
  return getTrialDaysLeft() === null;
}

export function isSubscribed(): boolean {
  return localStorage.getItem(IS_SUBSCRIBED_KEY) === 'true';
}

export function setSubscribed(value: boolean): void {
  localStorage.setItem(IS_SUBSCRIBED_KEY, value ? 'true' : 'false');
}

function readSubStatus(value: any): boolean | undefined {
  if (value === undefined || value === null) return undefined;
  const v = String(value).trim().toLowerCase();
  if (!v) return undefined;
  // Only treat *paid/active* states as "subscribed" for UI purposes.
  // Trial users should still see the trial status banner in the header.
  if (['active', 'subscribed', 'paid', 'succeeded', 'success'].includes(v)) return true;
  if (['trial', 'trialing', 'locked', 'lock', 'pending'].includes(v)) return false;
  if (['inactive', 'expired', 'canceled', 'cancelled', 'failed', 'past_due', 'past-due', 'unpaid'].includes(v))
    return false;
  return undefined;
}

function deriveSubscribedFromSubscription(subscription: any): boolean | undefined {
  if (!subscription || typeof subscription !== 'object') return undefined;
  const status = readSubStatus(
    subscription?.sub_status ??
      subscription?.subStatus ??
      subscription?.team_sub_status ??
      subscription?.teamSubStatus
  );
  if (status !== undefined) return status;

  return undefined;
}

export function normalizeUserFromSubscription(user: any): any {
  if (!user || typeof user !== 'object') return user;
  const subscription = user?.subscription;
  if (!subscription || typeof subscription !== 'object') return user;

  const sportAccess = subscription?.sport_access ?? subscription?.sportAccess;
  const subStatus = subscription?.sub_status ?? subscription?.subStatus;
  const subUntil = subscription?.sub_until ?? subscription?.subUntil;

  const nextUser: any = { ...user, subscription };

  if (Array.isArray(sportAccess) && sportAccess.length > 0) {
    nextUser.sports = sportAccess.map((code: any) => ({ code: String(code) }));
  } else if (sportAccess && typeof sportAccess === 'object') {
    // sport_access can be an object map like { MFB: {status, until}, ... }
    const codes = Object.keys(sportAccess);
    if (codes.length > 0) nextUser.sports = codes.map((code) => ({ code: String(code) }));
  }

  if (subStatus !== undefined || subUntil !== undefined) {
    nextUser.team = { ...(nextUser.team || {}), sub_status: subStatus, sub_until: subUntil };
  }

  const subscribed = deriveSubscribedFromSubscription(subscription);
  if (subscribed !== undefined) setSubscribed(subscribed);

  return nextUser;
}

export function getSportAccessRow(subscription: any, sportCode: string): any | null {
  const sportAccess = subscription?.sport_access ?? subscription?.sportAccess;
  if (!sportAccess || typeof sportAccess !== 'object') return null;
  return (sportAccess as any)?.[sportCode] ?? null;
}

export function isTrialExpiredForSport(
  subscription: any,
  sportCode: string,
  nowMs: number = Date.now()
): boolean {
  const row = getSportAccessRow(subscription, sportCode);
  if (!row) return false;
  const status = String(row?.status || '').toLowerCase();
  if (status === 'trial_end') return true;
  if (status !== 'trial') return false;
  const untilSec = Number(row?.until);
  if (!Number.isFinite(untilSec) || untilSec <= 0) return false;
  const nowSec = Math.floor(nowMs / 1000);
  return untilSec <= nowSec;
}
